import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { login, fireWebhook, storeSsoSession } from "../api/apiService";
import { ToastSuccess, ToastError } from "../components/toast-notification";

function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const xDeviceId = localStorage.getItem("x-device-service-id");
  const appName = localStorage.getItem("appName");

  const [formValues, setFormValues] = useState({ email: '', password: '', deviceId: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const localToken = localStorage.getItem("x-device-access-token");
    if (localToken) {
      let userId = 'user';
      try {
        const payload = JSON.parse(atob(localToken.split('.')[1]));
        userId = payload.userId || 'user';
      } catch (e) {}
      navigate(`/${userId}/profile`, { replace: true });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: '' });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    if (name === 'email') {
      if (!value) error = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(value)) error = 'Email address is invalid';
    }
    if (name === 'password') {
      if (!value) error = 'Password is required';
      else if (value.length < 6) error = 'Password must be at least 6 characters';
    }
    if (error) setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const validate = () => {
    let errors = {};
    if (!formValues.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formValues.email)) errors.email = 'Email address is invalid';
    if (!formValues.password) errors.password = 'Password is required';
    else if (formValues.password.length < 6) errors.password = 'Password must be at least 6 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const tryLogin = (bodyData) => {
    bodyData.deviceId = xDeviceId;
    login("email", bodyData)
      .then(async function (response) {
        const accessToken = response?.data?.data.accessToken;
        localStorage.setItem("x-device-access-token", accessToken);
        ToastSuccess(response?.data?.message);
        setIsLoading(false);

        const ssoRequestId = localStorage.getItem('sso_requestId');
        const ssoCodeChallenge = localStorage.getItem('sso_codeChallenge');
        if (ssoRequestId && ssoCodeChallenge) {
          try {
            await storeSsoSession({ requestId: ssoRequestId, accessToken, deviceId: xDeviceId, codeChallenge: ssoCodeChallenge });
          } catch (e) {
            console.error('Failed to store SSO session:', e);
          } finally {
            localStorage.removeItem('sso_requestId');
            localStorage.removeItem('sso_codeChallenge');
          }
        }

        const redirectUrl = localStorage.getItem("redirect");
        const storedWebhook = localStorage.getItem("webhook");

        if (redirectUrl) {
          localStorage.removeItem("redirect");
          localStorage.removeItem("webhook");
          localStorage.removeItem("appId");
          localStorage.removeItem("appName");

          if (storedWebhook) {
            await fireWebhook(storedWebhook, accessToken, xDeviceId);
          }

          window.location.replace(`${redirectUrl}?accessToken=${accessToken}&xDeviceId=${xDeviceId}`);
        } else {
          let userId = 'user';
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            userId = payload.userId || 'user';
          } catch (e) {}

          if (appName) {
            localStorage.removeItem("appId");
            localStorage.removeItem("appName");
            navigate(`/${userId}/profile`, { replace: true, state: { ssoAuthSuccess: true, appName: appName } });
          } else {
            window.location.href = `/${userId}/profile`;
          }
        }
      })
      .catch(function (error) {
        ToastError(error?.response?.data?.message);
        setIsLoading(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setIsLoading(true);
      tryLogin(formValues);
    }
  };

  const googleLogin = (e) => {
    e.preventDefault();
    const url = `${process.env.REACT_APP_BASE_URL}/social-login/google`;
    const fromUrl = `${window.location.origin}/authorize-callback`;
    const appName = process.env.REACT_APP_NAME;
    window.location.href = `${url}?appName=${appName}&fromUrl=${fromUrl}&deviceId=${xDeviceId}`;
  };

  const githubLogin = (e) => {
    e.preventDefault();
    const url = `${process.env.REACT_APP_BASE_URL}/social-login/github`;
    const fromUrl = `${window.location.origin}/authorize-callback`;
    const appName = process.env.REACT_APP_NAME;
    window.location.href = `${url}?appName=${appName}&fromUrl=${fromUrl}&deviceId=${xDeviceId}`;
  };

  return (
    <div className="min-h-screen flex overflow-hidden bg-[#030305]">
      
      {/* LEFT PANEL: Cyberpunk Visuals */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 border-r border-cyan-900/30">
        <div className="absolute inset-0 cyber-auth-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#030305]" />
        
        <div className="relative z-10 flex items-center gap-4 mb-auto">
          <div className="w-10 h-10 border border-cyan-400 bg-cyan-900/30 flex items-center justify-center" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}>
            <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <span className="text-2xl font-black text-cyan-400 tracking-widest uppercase drop-shadow-[0_0_8px_rgba(0,255,255,0.4)]">
            EXTENDSWARE
          </span>
        </div>

        <div className="relative z-10 my-auto">
          <h2 className="text-[4rem] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 to-fuchsia-500 drop-shadow-[0_0_15px_rgba(0,255,255,0.3)]">
            MAINFRAME
            <br />
            ACCESS
          </h2>
          <div className="mt-6 flex flex-col gap-2 font-mono text-cyan-500/60 text-xs">
            <p>> INITIALIZING SECURE UPLINK...</p>
            <p>> BYPASSING FIREWALL PROXIES... [OK]</p>
            <p>> AWAITING OPERATOR CREDENTIALS...</p>
            <div className="h-4 w-32 bg-cyan-500/20 border border-cyan-500/30 mt-2 relative overflow-hidden">
              <div className="absolute top-0 left-0 h-full w-2/3 bg-cyan-500/50 animate-pulse" />
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-auto flex items-center justify-between text-[10px] font-mono text-cyan-900">
          <span>SYS.VER: 9.4.2</span>
          <span>ENCRYPTION: 256-BIT NEURAL</span>
        </div>
      </div>

      {/* RIGHT PANEL: Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/10 via-[#030305] to-[#030305] lg:hidden" />
        
        <div className="w-full max-w-md cyber-card p-8 md:p-10 relative z-10 shadow-none border-none lg:bg-transparent lg:backdrop-filter-none">
          
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-2xl font-black text-white tracking-widest uppercase drop-shadow-[0_0_8px_rgba(255,255,255,0.3)] mb-2">
              {appName ? (
                <>UPLINK TO <span className="text-cyan-400 drop-shadow-[0_0_8px_rgba(0,255,255,0.6)]">{appName}</span></>
              ) : (
                "SYS.AUTH_REQUIRED"
              )}
            </h1>
            <p className="cyber-label text-[10px]">
              {appName ? "PROVIDE CREDENTIALS TO ESTABLISH CONNECTION." : "ENTER CREDENTIALS TO ACCESS MAINFRAME."}
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="cyber-label block mb-2">IDENTIFIER [EMAIL]</label>
              <input id="email" name="email" type="email" autoComplete="email"
                className={`cyber-input w-full p-3 font-mono ${formErrors.email ? "border-red-500 shadow-[0_0_10px_rgba(255,0,0,0.3)]" : ""}`}
                placeholder="operator@sys.net" value={formValues.email} onChange={handleChange} onBlur={handleBlur} />
              {formErrors.email && <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest">
                [!] {formErrors.email}
              </p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="cyber-label block">SECURITY_KEY [PASSWORD]</label>
                <a href="/forgot-password" className="text-[10px] font-bold text-fuchsia-400 hover:text-fuchsia-300 uppercase tracking-widest transition-colors">FORGOT KEY?</a>
              </div>
              <input id="password" name="password" type="password"
                className={`cyber-input w-full p-3 font-mono ${formErrors.password ? "border-red-500 shadow-[0_0_10px_rgba(255,0,0,0.3)]" : ""}`}
                placeholder="********" value={formValues.password} onChange={handleChange} onBlur={handleBlur} />
              {formErrors.password && <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest">
                [!] {formErrors.password}
              </p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading}
              className="cyber-btn w-full py-4 mt-2">
              {isLoading ? "AUTHENTICATING..." : "INITIATE_LOGIN"}
            </button>
          </form>

          {/* Divider */}
          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-cyan-900/50" />
            <span className="cyber-label text-[9px]">OR EXTERNAL AUTH</span>
            <div className="flex-1 h-px bg-cyan-900/50" />
          </div>

          {/* Social */}
          <div className="grid grid-cols-2 gap-4">
            <button onClick={googleLogin} className="cyber-btn py-3 text-xs flex items-center justify-center gap-2 border-cyan-800 text-cyan-500 hover:border-cyan-400 hover:text-cyan-400">
              GGL_AUTH
            </button>
            <button onClick={githubLogin} className="cyber-btn py-3 text-xs flex items-center justify-center gap-2 border-cyan-800 text-cyan-500 hover:border-cyan-400 hover:text-cyan-400">
              GHB_AUTH
            </button>
          </div>

          <p className="mt-8 text-center text-[10px] font-bold uppercase tracking-widest text-gray-500">
            NO ACCESS?{" "}
            <a href="/create-account" className="text-cyan-400 hover:text-cyan-300 drop-shadow-[0_0_5px_rgba(0,255,255,0.4)] ml-1">REGISTER_NEW_NODE</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
